import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  FieldTitle,
  Form,
  type Identifier,
  required,
  useDataProvider,
  useGetList,
  useInput,
  useNotify,
  useTranslate,
} from "ra-core";
import type { FieldValues } from "react-hook-form";
import { useWatch } from "react-hook-form";

import { Confirm } from "@/components/admin/confirm";
import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "@/components/admin/form";
import { InputHelperText } from "@/components/admin/input-helper-text";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";

import { StatusPill } from "../design/StatusPill";
import type { DealActivityResult, DealActivityType } from "../types";
import {
  RESULT_LABEL_MAX_LENGTH,
  computeNextStepRuleSummary,
  nextResultPosition,
  orderActivityResultsForList,
  toActivityResultErrorMessage,
  validateNextStepOffsetHours,
  validateResultLabel,
  validateResultMessageTemplate,
} from "./activityResultForm";
import { MESSAGE_TEMPLATE_MAX_LENGTH } from "./activityTypeForm";
import { MessageTemplatePreview } from "./MessageTemplatePreview";

/**
 * Almikia Epic EA (TASK-114): "Resultados posibles" -- inside the same
 * direccion-only edit screen as TASK-113 (`DealActivityTypeEdit.tsx`), the
 * possible results of an activity, each carrying its "next step and when"
 * rule (DATA CONTRACT EA (2)). Neither a result nor its rule is ever deleted:
 * archiving sets `archived_at`, reactivating clears it, mirroring
 * `ActivityTypeArchiveActions` in `DealActivityTypeEdit.tsx`. Saving a rule
 * change invalidates the `deal_activity_results` react-query cache so the
 * TASK-111 `LogActivityForm`'s next-step preview reflects it immediately,
 * without a page reload (ticket acceptance criterion).
 */
export function ActivityResultsSection({
  activityTypeId,
  activityTypes,
}: {
  activityTypeId: Identifier;
  activityTypes: DealActivityType[];
}) {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const [formTarget, setFormTarget] = useState<
    { mode: "create" } | { mode: "edit"; result: DealActivityResult } | null
  >(null);
  const [archiveTarget, setArchiveTarget] = useState<DealActivityResult | null>(
    null,
  );
  const [isArchiving, setIsArchiving] = useState(false);

  const {
    data: results,
    isPending,
    refetch,
  } = useGetList<DealActivityResult>("deal_activity_results", {
    filter: { activity_type_id: activityTypeId },
    pagination: { page: 1, perPage: 200 },
    sort: { field: "position", order: "ASC" },
  });

  if (isPending || !results) {
    return <Skeleton className="h-24 w-full" />;
  }

  const ordered = orderActivityResultsForList(results);
  const activityTypeById = new Map(activityTypes.map((a) => [a.id, a]));

  const invalidateResults = async () => {
    queryClient.invalidateQueries({ queryKey: ["deal_activity_results"] });
    await refetch();
  };

  const closeForm = () => setFormTarget(null);

  const handleSubmitResult = async (data: FieldValues) => {
    const label = data.label as string;
    const messageTemplate = data.message_template as string | null | undefined;
    const rawNextActivityTypeId = data.next_activity_type_id as
      | Identifier
      | string
      | null
      | undefined;
    const rawOffsetHours = data.next_step_offset_hours as
      | number
      | string
      | null
      | undefined;

    const nextActivityTypeId =
      rawNextActivityTypeId == null || rawNextActivityTypeId === ""
        ? null
        : Number(rawNextActivityTypeId);
    const payload = {
      label: label.trim(),
      message_template: messageTemplate || null,
      next_activity_type_id: nextActivityTypeId,
      next_step_offset_hours:
        nextActivityTypeId == null ? null : Number(rawOffsetHours),
    };

    try {
      if (formTarget?.mode === "edit") {
        await dataProvider.update("deal_activity_results", {
          id: formTarget.result.id,
          data: payload,
          previousData: formTarget.result,
        });
        notify("resources.deal_activity_results.notifications.updated", {
          type: "success",
        });
      } else {
        await dataProvider.create("deal_activity_results", {
          data: {
            ...payload,
            activity_type_id: activityTypeId,
            position: nextResultPosition(results),
          },
        });
        notify("resources.deal_activity_results.notifications.created", {
          type: "success",
        });
      }
      await invalidateResults();
      closeForm();
    } catch (error) {
      notify(toActivityResultErrorMessage(error), { type: "error" });
    }
    return undefined;
  };

  const handleArchiveToggle = async () => {
    if (!archiveTarget) return;
    setIsArchiving(true);
    try {
      await dataProvider.update("deal_activity_results", {
        id: archiveTarget.id,
        data: {
          archived_at:
            archiveTarget.archived_at == null ? new Date().toISOString() : null,
        },
        previousData: archiveTarget,
      });
      await invalidateResults();
      notify(
        archiveTarget.archived_at == null
          ? "resources.deal_activity_results.archive.archived"
          : "resources.deal_activity_results.archive.reactivated",
        { type: "success" },
      );
    } catch (error) {
      notify(toActivityResultErrorMessage(error), { type: "error" });
    } finally {
      setIsArchiving(false);
      setArchiveTarget(null);
    }
  };

  const activeLabelsExcludingEdited = results
    .filter(
      (result) =>
        result.archived_at == null &&
        !(formTarget?.mode === "edit" && result.id === formTarget.result.id),
    )
    .map((result) => result.label);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {translate("resources.deal_activity_results.title")}
        </h3>
        <Button
          type="button"
          size="sm"
          className="min-h-11"
          onClick={() => setFormTarget({ mode: "create" })}
        >
          <Plus className="size-4 mr-1" />
          {translate("resources.deal_activity_results.action.new_result")}
        </Button>
      </div>

      {ordered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          {translate("resources.deal_activity_results.empty")}
        </p>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {ordered.map((result) => {
              const nextActivity =
                result.next_activity_type_id != null
                  ? activityTypeById.get(result.next_activity_type_id)
                  : undefined;
              const summary = computeNextStepRuleSummary(
                result,
                nextActivity?.name,
              );
              const isArchived = result.archived_at != null;
              const nextActivityArchived = nextActivity?.archived_at != null;

              return (
                <div key={result.id} className="flex items-center gap-3 p-4">
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left"
                    onClick={() => setFormTarget({ mode: "edit", result })}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium truncate">
                        {result.label}
                      </span>
                      {isArchived ? (
                        <StatusPill
                          tone="not-ok"
                          text={translate(
                            "resources.deal_activity_results.archived_marker",
                          )}
                        />
                      ) : null}
                      {nextActivityArchived ? (
                        <StatusPill
                          tone="not-ok"
                          text={translate(
                            "resources.deal_activity_results.next_activity_archived_marker",
                          )}
                        />
                      ) : null}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {summary.hasNextStep
                        ? translate(
                            "resources.deal_activity_results.rule_summary",
                            {
                              activity: summary.nextActivityName ?? "",
                              hours: summary.hours ?? 0,
                            },
                          )
                        : translate(
                            "resources.deal_activity_results.rule_summary_none",
                          )}
                    </p>
                  </button>
                  <Button
                    type="button"
                    size="sm"
                    variant={isArchived ? "default" : "outline"}
                    className="min-h-11 shrink-0"
                    onClick={() => setArchiveTarget(result)}
                  >
                    {isArchived
                      ? translate(
                          "resources.deal_activity_results.action.reactivate",
                        )
                      : translate(
                          "resources.deal_activity_results.action.archive",
                        )}
                  </Button>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <ActivityResultFormSheet
        open={formTarget != null}
        onOpenChange={(open) => {
          if (!open) closeForm();
        }}
        editingResult={formTarget?.mode === "edit" ? formTarget.result : null}
        activityTypeId={activityTypeId}
        activityTypes={activityTypes}
        activeLabels={activeLabelsExcludingEdited}
        onSubmit={handleSubmitResult}
      />

      <Confirm
        isOpen={archiveTarget != null}
        loading={isArchiving}
        title={
          archiveTarget?.archived_at == null
            ? "resources.deal_activity_results.archive.confirm_archive_title"
            : "resources.deal_activity_results.archive.confirm_reactivate_title"
        }
        content={
          archiveTarget?.archived_at == null
            ? "resources.deal_activity_results.archive.confirm_archive_body"
            : "resources.deal_activity_results.archive.confirm_reactivate_body"
        }
        confirm={
          archiveTarget?.archived_at == null
            ? "resources.deal_activity_results.action.archive"
            : "resources.deal_activity_results.action.reactivate"
        }
        confirmColor={
          archiveTarget?.archived_at == null ? "warning" : "primary"
        }
        onConfirm={handleArchiveToggle}
        onClose={() => setArchiveTarget(null)}
      />
    </div>
  );
}

interface ActivityResultFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingResult: DealActivityResult | null;
  activityTypeId: Identifier;
  activityTypes: DealActivityType[];
  activeLabels: string[];
  onSubmit: (data: FieldValues) => Promise<void>;
}

/**
 * The add/edit sheet for a single result. Not wrapped in `CreateBase`/
 * `EditBase` (both add and edit share this one sheet, mounted once by
 * `ActivityResultsSection` and toggled by `editingResult`) -- `onSubmit` goes
 * straight to `ra-core`'s `<Form>`, which supports it standalone. Remounted
 * (via `key`) on every open so a stale `next_step_offset_hours` display value
 * from a previous result never leaks into the next one.
 */
const ActivityResultFormSheet = ({
  open,
  onOpenChange,
  editingResult,
  activityTypeId,
  activityTypes,
  activeLabels,
  onSubmit,
}: ActivityResultFormSheetProps) => {
  const translate = useTranslate();

  // DATA CONTRACT EA (2): "any non-archived activity including the one being
  // edited" -- the current activity is always offered even in the unlikely
  // case it is itself archived.
  const nextActivityChoices = activityTypes
    .filter(
      (activity) =>
        activity.archived_at == null || activity.id === activityTypeId,
    )
    .map((activity) => ({ id: activity.id, name: activity.name }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-dvh flex flex-col [&_[data-slot=sheet-close]]:text-sidebar-foreground"
        aria-describedby={undefined}
      >
        <Form
          key={editingResult?.id ?? "create"}
          record={
            editingResult ?? {
              label: "",
              message_template: null,
              next_activity_type_id: null,
              next_step_offset_hours: null,
            }
          }
          onSubmit={onSubmit}
          className="h-dvh flex-1 flex flex-col"
        >
          <SheetHeader className="bg-sidebar text-sidebar-foreground border-b">
            <SheetTitle className="text-sidebar-foreground text-xl font-semibold">
              {editingResult
                ? translate("resources.deal_activity_results.title_edit")
                : translate("resources.deal_activity_results.title_new")}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto flex flex-col gap-4 p-4">
            <TextInput
              source="label"
              label="resources.deal_activity_results.fields.label"
              validate={[
                required(),
                (value: string) => validateResultLabel(value, activeLabels),
              ]}
              maxLength={RESULT_LABEL_MAX_LENGTH}
              helperText={false}
            />
            <TextInput
              source="message_template"
              label="resources.deal_activity_results.fields.message_template"
              multiline
              rows={4}
              validate={validateResultMessageTemplate}
              maxLength={MESSAGE_TEMPLATE_MAX_LENGTH}
              helperText="resources.deal_activity_results.fields.message_template_help"
            />
            <MessageTemplatePreview source="message_template" />
            <SelectInput
              source="next_activity_type_id"
              label="resources.deal_activity_results.fields.next_activity_type_id"
              choices={nextActivityChoices}
              emptyText="resources.deal_activity_results.fields.no_next_activity"
              helperText={false}
              className="[&_[data-slot=select-trigger]]:h-11"
            />
            <NextStepOffsetInput />
          </div>

          <SheetFooter className="border-t flex flex-row w-full gap-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1 h-12"
              onClick={() => onOpenChange(false)}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button type="submit" variant="gradient" className="flex-1 h-12">
              {translate("ra.action.save")}
            </Button>
          </SheetFooter>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

/**
 * `next_step_offset_hours` -- required exactly when `next_activity_type_id`
 * is set, cleared automatically when it is removed (ticket acceptance
 * criterion): a hand-rolled input for the same reason as `AttemptCapInput`/
 * `TargetTimeInput` (a plain `<NumberInput>` can't represent "empty" as
 * `null`).
 */
const NextStepOffsetInput = ({
  source = "next_step_offset_hours",
  nextActivitySource = "next_activity_type_id",
}: {
  source?: string;
  nextActivitySource?: string;
}) => {
  const nextActivityTypeId = useWatch({ name: nextActivitySource });
  const hasNextActivity =
    nextActivityTypeId != null && nextActivityTypeId !== "";

  const { id, field, fieldState } = useInput({
    source,
    validate: (value: number | string | null | undefined) =>
      validateNextStepOffsetHours(value, hasNextActivity),
  });

  const [displayValue, setDisplayValue] = useState<string>(
    field.value != null ? String(field.value) : "",
  );

  useEffect(() => {
    if (!hasNextActivity) {
      setDisplayValue("");
      field.onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNextActivity]);

  const handleChange = (nextValue: string) => {
    setDisplayValue(nextValue);
    field.onChange(nextValue === "" ? null : Number(nextValue));
  };

  return (
    <FormField id={id} name={field.name}>
      <FormLabel>
        <FieldTitle
          label="resources.deal_activity_results.fields.next_step_offset_hours"
          source={source}
          isRequired={hasNextActivity}
        />
      </FormLabel>
      <FormControl>
        <Input
          type="number"
          className="w-24 h-11 tabular-nums"
          disabled={!hasNextActivity}
          value={displayValue}
          onChange={(event) => handleChange(event.target.value)}
          onBlur={field.onBlur}
        />
      </FormControl>
      <InputHelperText
        helperText={
          fieldState.error
            ? false
            : "resources.deal_activity_results.fields.next_step_offset_help"
        }
      />
      <FormError />
    </FormField>
  );
};
